import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GenerateCourseModuleList } from "../../../utils/authoring/course/generateCourseModuleList";
import { CourseType } from "../../../pages/course-authoring/course-type";

interface ChapterSelection {
  moduleId: string | null;
  chapterId: string | null | undefined;
}

interface previewDataType {
  moduleId: string;
  chapterId: string;
  contentId: string;
  contentType: string;
  weblink: string;
  artifactId: string;
}

interface CourseState {
  data: CourseType;
  selectedChapter: ChapterSelection | null;
  previewData: previewDataType;
  fetchCourse: boolean;
  fetchContent: boolean;
  isRearranging: boolean;
}

const courseData: CourseType = {
  pkId: 0,
  id: "",
  org: "gl-india",
  createdZoneId: "Asia/Kolkata",
  updatedZoneId: "Asia/Kolkata",
  createdby: "",
  createdat: "",
  updatedby: "",
  updatedat: "",
  templateId: null,
  clonedId: null,
  title: "",
  socialInteractions: [],
  shortDescription: "",
  longDescription: "",
  prerequisite: "",
  learningObjective: "",
  demonstrableOutcome: "",
  learningassistant: true,
  searchTags: {
    defaultTag: [""]
  },
  skills: [],
  difficultyLevel: "",
  duration: 0,
  type: "",
  access: [],
  associatedRoles: [],
  launchDate: "",
  expiryDate: "",
  status: "",
  stakeholder: {
    owner: [""],
    reviewer: [],
    observer: [],
    publisher: [],
    learner: []
  },
  language: "",
  publishedat: null,
  publishedby: null,
  sourceName: "",
  sourceType: "",
  branding: {
    promo: [],
    banner: [],
    thumbnail: []
  },
  tempExternalDataFores: {},
  category: "",
  cataloged: true,
  isCertificate: true,
  brandingurls: null,
  artifacturl: null,
  skillnamelist: null,
  moduleList: [
    {
      pkId: 0,
      id: "",
      org: "",
      createdZoneId: "Asia/Kolkata",
      updatedZoneId: "Asia/Kolkata",
      createdby: "",
      createdat: "",
      updatedby: "",
      updatedat: "",
      title: "",
      shortDescription: "",
      searchTags: {
        defaultTag: []
      },
      skills: [],
      courseId: "",
      status: "",
      locked: false,
      lockedType: "",
      accessEnabled: false,
      accessCriteria: {
        joblevel: [""],
        progress: 0,
        userAccess: {
          cohort: [],
          userId: [],
          userGroup: []
        }
      },
      lockedModuleId: null,
      lockedContentId: null,
      chapterList: [
        {
          shortdescription: "",
          pkid: 0,
          artifacttext: [],
          updatedby: "",
          org: "gl-india",
          updatedzoneid: "Asia/Kolkata",
          _meta: {},
          title: "javascript",
          launchtype: "image",
          duration: 10,
          archived: false,
          createdat: "2024-07-19T07:11:29.253645+00:00",
          artifacttype: "course_artifact_image",
          createdzoneid: "Asia/Kolkata",
          createdby: "adf0d065-2494-4619-aadc-7ac8a5cc5962",
          weblink: "",
          artifactid: 4253,
          id: "04799342-60ad-4e9d-af0f-4b092e0aee5f",
          status: "published",
          updatedat: "2024-07-19T07:11:29.253645+00:00",
          moduleid: "2e841567-0397-4ce0-b5d2-b57073fe3cc3",
          orderInModule: 1,
          locked: false,
          lockedType: "free",
          accessEnabled: false,
          videosettings: {
            pauseOnOutOfFocus: true,
            allowResumePlayback: true,
            allowUnmute: true,
            allowSeek: true,
            allowPlaybackSpeedControl: true
          },
          accessCriteria: {
            joblevel: [],
            progress: 0,
            userAccess: {
              cohort: [],
              userId: [],
              userGroup: []
            }
          },
          contentType: "course_artifact_image",
          lockedModuleId: null,
          contentId: "7d1de737-26bb-491e-8b26-5d7612b1e955",
          lockedContentId: null
        }
      ],
      connectionList: [
        {
          pkId: 463,
          id: "2614cd6e-e569-42f6-99e1-31f0149bf321",
          org: "gl-india",
          createdZoneId: "Asia/Kolkata",
          updatedZoneId: "Asia/Kolkata",
          createdby: "adf0d065-2494-4619-aadc-7ac8a5cc5962",
          createdat: "2024-09-09T10:14:31.254541+00:00",
          updatedby: "adf0d065-2494-4619-aadc-7ac8a5cc5962",
          updatedat: "2024-09-09T10:15:14.504331+00:00",
          title: "",
          sourceModuleId: "2e841567-0397-4ce0-b5d2-b57073fe3cc3",
          nextModuleId: "3c0b71d3-de0b-4af8-b75b-d23207784986",
          courseId: "031fdf50-f47e-4f5c-b893-1e3146988c9b",
          archived: false
        }
      ]
    }
  ],
  contentOccurences: {
    course_artifact_video: 1,
    course_artifact_pdf: 1,
    course_artifact_image: 3
  }
};

const initialState: CourseState = {
  data: courseData,
  selectedChapter: null,
  previewData: {
    moduleId: "",
    chapterId: "",
    contentId: "",
    contentType: "",
    weblink: "",
    artifactId: ""
  },
  fetchCourse: true,
  fetchContent: true,
  isRearranging: false
};

export const CourseAuthoringSlice = createSlice({
  name: "CourseAuthoringSlice",
  initialState,
  reducers: {
    setData(state, action) {
      const generatedModuleList = GenerateCourseModuleList(
        action.payload.moduleList
      );
      const updatedData = {
        ...action.payload,
        moduleList: generatedModuleList
      };
      return { ...state, data: updatedData, fetchCourse: false };
    },
    setFetchingContentState(
      state: CourseState,
      action: PayloadAction<boolean>
    ) {
      state.fetchContent = action.payload;
    },
    selectChapter(state, action) {
      const { moduleId, chapterId } = action.payload;
      state.selectedChapter = { moduleId, chapterId };
    },
    selectDefaultChapter(state, action) {
      const { moduleId, chapterId } = action.payload;
      state.selectedChapter = { moduleId, chapterId };
    },
    deleteChapter(state: CourseState, action: PayloadAction<ChapterSelection>) {
      const { moduleId, chapterId } = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          moduleList: state.data.moduleList.map((module: any) => {
            if (module.id === moduleId) {
              return {
                ...module,
                chapterList: module.chapterList.filter(
                  (chapter: any) => chapter.tempId !== chapterId
                )
              };
            }
            return module;
          })
        }
      };
    },
    setPreviewData(state: CourseState, action) {
      const {
        moduleId,
        chapterId,
        contentType,
        contentId,
        weblink,
        artifactId
      } = action.payload;
      state.previewData.moduleId = moduleId;
      state.previewData.chapterId = chapterId;
      state.previewData.contentType = contentType;
      state.previewData.contentId = contentId;
      state.previewData.weblink = weblink;
      state.previewData.artifactId = artifactId;
    },

    setIsRearranging(state) {
      state.isRearranging = true;
    },
    resetIsRearranging(state) {
      state.isRearranging = false;
    },
    resetSlice(state) {
      state.previewData = {
        moduleId: "",
        chapterId: "",
        contentId: "",
        contentType: "",
        weblink: "",
        artifactId: ""
      };
      state.selectedChapter = null;
      state.data = courseData;
    }
  }
});

export default CourseAuthoringSlice;
export const CourseAuthoringSliceActions = CourseAuthoringSlice.actions;
